/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import { type ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common/types';
import { createEditorToolbarActions } from '@atlaskit/native-embeds-common/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getInitialThemeParam } from '@atlassian/native-embeds-core/theme';
import {
	type Experience,
	type ExperienceManifest,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';
import type { ActionContext } from '@atlassian/native-embeds-core/types/action-context';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core/types/features/main';

import { ChartLoading } from './chart-loading';
import { DataFetchControlLoader } from './data-fetch-control-loader';
import { DATA_FETCH_FEATURE } from './data-fetch-protocol';
import { AVPVIZ_EXPERIENCE_MANIFEST_ID } from './manifest-id';

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const AVPVIZ_CHART_URL_PATTERN = new RegExp(
	`/(?:avpviz|dashboards)/c/${UUID}/w/${UUID}/d/${UUID}/(?:chart|widget)/${UUID}/?(?:[?#].*)?$`,
);

const isAvpvizChartUrl = (url: string): boolean =>
	fg('platform_avp_viz_confluence_native_embed') && AVPVIZ_CHART_URL_PATTERN.test(url);

/**
 * Experience manifest for a shared AVPViz chart.
 *
 * This claims only canonical shared chart URLs while its rollout gate is enabled.
 * The linking-platform resolver supplies the title and preview used by the host overlay.
 */
export const AvpvizExperienceManifest: {
	readonly enforceQueryParams: true;
	readonly handleChildCommands: (
		_experience: Experience,
		_command: NativeEmbedChildCommand,
	) => void;
	readonly id: typeof AVPVIZ_EXPERIENCE_MANIFEST_ID;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly queryParams: { readonly hideBorder: 'true'; readonly nativeEmbedView: 'true' };
	readonly resolveViaLinkingPlatform: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		readonly editorToolbarHandlers: {};
		readonly frameElevation: 'raised';
		readonly headerControls: {
			readonly hideTitle: true;
			readonly renderBeforeActions: (context: ActionContext) => JSX.Element;
		};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showBorder: false;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly useResolvedTitleIcon: true;
	};
} = {
	id: AVPVIZ_EXPERIENCE_MANIFEST_ID,
	enforceQueryParams: true,
	queryParams: { nativeEmbedView: 'true', hideBorder: 'true' },
	handleChildCommands: (_experience: Experience, _command: NativeEmbedChildCommand): void => {
		// Toolbar state is delivered through the experience child-command subscription.
	},
	isUrlMatch: isAvpvizChartUrl,
	isActive: (): true => true,
	resolveViaLinkingPlatform: true,
	supportedFeatures: new Set<NativeEmbedFeature>([
		...allNativeEmbedCoreFeatures,
		DATA_FETCH_FEATURE,
	]),
	transformUrl: (url: string): string => {
		const urlObj = new URL(url);
		urlObj.searchParams.set('hideBorder', 'true');
		urlObj.searchParams.set('nativeEmbedView', 'true');

		const initialTheme = getInitialThemeParam();
		if (initialTheme) {
			urlObj.searchParams.set('themeState', initialTheme);
		}

		return urlObj.toString();
	},
	uiConfig: {
		headerControls: {
			hideTitle: true,
			renderBeforeActions: (context: ActionContext) => (
				<DataFetchControlLoader key="avpviz-data-fetch" context={context} />
			),
		},
		showBorder: false,
		frameElevation: 'raised',
		editorToolbarActions: (): ManifestEditorToolbarActions<any> =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				],
				moreItems: [BUILTIN_TOOLBAR_KEYS.COPY, BUILTIN_TOOLBAR_KEYS.DELETE],
			}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<ChartLoading {...props} />
		),
		titleIcon: ChartBarIcon,
		useResolvedTitleIcon: true,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
